import { api } from './client';
import type { User, AuthTokens, AuditLogEntry, AdminUserListParams } from '@/types';
import { useAuthStore } from '@/stores/authStore';

interface LoginRequest {
  email: string;
  password: string;
  /** Required on the second login step when email 2FA is enabled. */
  otp_code?: string;
}

// Backend contract: login returns a flat session payload, or
// `{ requires_otp: true }` when email 2FA is enabled and no code was sent yet.
interface LoginResponse {
  user: User;
  token: string;
  refresh_token: string;
  expires_at: string;
  requires_otp?: false;
  // Legacy routes.go shape (pre-fix); kept so older API builds still work in dev.
  session?: {
    token?: string;
    refresh_token?: string;
    expires_at?: string;
  };
}

export interface RequiresOTPResponse {
  requires_otp: true;
  message?: string;
  /** Present when SMTP is not configured (dev only). */
  dev_code?: string;
}

export type LoginResult = LoginResponse | RequiresOTPResponse;

export function isRequiresOTP(result: LoginResult): result is RequiresOTPResponse {
  return result.requires_otp === true;
}

interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

class AuthService {
  async login(request: LoginRequest): Promise<LoginResult> {
    try {
      const response = await api.post<LoginResult>('/auth/login', request);
      return response.data;
    } catch (error: unknown) {
      // Backend may return 401 with requires_otp when a code was already
      // emailed (cooldown) or OTP delivery failed after password check.
      const data = (error as { response?: { data?: RequiresOTPResponse & { error?: string } } })
        ?.response?.data;
      if (data?.requires_otp) {
        return {
          requires_otp: true,
          message: data.message || data.error,
          dev_code: data.dev_code,
        };
      }
      throw error;
    }
  }

  async register(request: RegisterRequest): Promise<User> {
    const username = request.email.split('@')[0];
    const response = await api.post<User>('/auth/register', {
      email: request.email,
      username,
      password: request.password,
      full_name: request.name,
    });
    return response.data;
  }

  async logout(): Promise<void> {
    const tokens = useAuthStore.getState().tokens;
    if (tokens?.token) {
      try {
        await api.post('/auth/logout', {}, {
          headers: { Authorization: `Bearer ${tokens.token}` }
        });
      } catch {
        // Continue with local logout even if backend call fails
      }
    }
    // Always clear local state regardless of backend result
    localStorage.removeItem('khiimori-auth');
    useAuthStore.setState({
      user: null,
      tokens: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
  }

  async refresh(refreshToken: string): Promise<AuthTokens> {
    const response = await api.post<AuthTokens>('/auth/refresh', { refresh_token: refreshToken });
    return response.data;
  }

  async getUser(): Promise<User> {
    const response = await api.get<User>('/users/me');
    return response.data;
  }

  async updateUser(data: Partial<User>): Promise<User> {
    const payload: Record<string, unknown> = { ...data };
    const fullName = data.full_name ?? data.name;
    if (fullName !== undefined) {
      payload.full_name = fullName;
    }
    delete payload.name;
    const response = await api.put<User>('/users/me', payload);
    return response.data;
  }

  async deleteUser(): Promise<void> {
    await api.delete('/users/me');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.post(`/users/me/password`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  async getUserSessions(): Promise<unknown[]> {
    const response = await api.get<{ sessions: unknown[] }>(`/users/me/sessions`);
    return response.data.sessions;
  }

  async listUsers(params?: AdminUserListParams): Promise<User[]> {
    const response = await api.get<{ users: User[] }>('/users', { params });
    return response.data.users;
  }

  async updateUserRole(userId: string, role: User['role']): Promise<void> {
    await api.put(`/users/${userId}/role`, { role });
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<void> {
    await api.put(`/users/${userId}/status`, { is_active: isActive });
  }

  async adminResetUserPassword(userId: string): Promise<{ message: string; dev_reset_url?: string }> {
    const response = await api.post<{ message: string; dev_reset_url?: string }>(
      `/users/${userId}/password-reset`,
    );
    return response.data;
  }

  async adminRevokeUserSessions(userId: string): Promise<void> {
    await api.post(`/users/${userId}/sessions/revoke-all`);
  }

  async adminDeleteUser(userId: string): Promise<void> {
    await api.delete(`/users/${userId}`);
  }

  async adminRestoreUser(userId: string): Promise<void> {
    await api.post(`/users/${userId}/restore`);
  }

  async listAdminAuditLogs(limit = 50): Promise<AuditLogEntry[]> {
    const response = await api.get<{ logs: AuditLogEntry[] }>('/admin/audit-logs', {
      params: { limit },
    });
    return response.data.logs ?? [];
  }

  async revokeAllSessions(): Promise<{ count: number }> {
    const response = await api.post<{ message: string; count: number }>(`/users/me/sessions/revoke-all`);
    return { count: response.data.count };
  }

  async requestPasswordReset(email: string): Promise<{ message?: string; dev_reset_url?: string }> {
    const response = await api.post<{ message?: string; dev_reset_url?: string }>('/auth/password-reset/request', { email });
    return response.data;
  }

  async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    await api.post('/auth/password-reset/confirm', { token, new_password: newPassword });
  }

  async requestEmailVerification(): Promise<string> {
    const response = await api.post<{ token?: string }>('/users/me/verify-email/request');
    return response.data.token ?? '';
  }

  async verifyEmail(token: string): Promise<void> {
    await api.post('/auth/verify-email', { token });
  }

  // Email OTP 2FA — see internal/api/handlers/twofa_handler.go.

  async getTwoFAStatus(): Promise<{ enabled: boolean }> {
    const response = await api.get<{ enabled: boolean }>('/auth/2fa/status');
    return response.data;
  }

  async enableTwoFA(): Promise<void> {
    await api.post('/auth/2fa/enable');
  }

  async disableTwoFA(): Promise<void> {
    await api.post('/auth/2fa/disable');
  }

  // Transform login response to match authStore expectations.
  // Backend returns a flat `AuthResponse` (see auth_handler.go).
  transformLoginResponse(response: LoginResponse): { user: User; token: string; refresh_token: string; expires_at: string } {
    const session = response.session;
    return {
      user: response.user,
      token: response.token || session?.token || '',
      refresh_token: response.refresh_token || session?.refresh_token || '',
      expires_at: response.expires_at || session?.expires_at || '',
    };
  }
}

export const authService = new AuthService();